<form [formGroup]="frmFields">

  <!-- VEHICLE TYPE -->
  <div class="row mt-2 mb-3">
    <div class="col-md-4">
      <mat-form-field class="w-100">
        <mat-label>Class of Vehicle</mat-label>
        <mat-select formControlName="type" required
          [ngClass]="{ 'is-invalid': (f.type?.touched || submitted) && f.type?.errors?.required }">
          <mat-option value="">— Select vehicle type —</mat-option>
          <mat-option *ngFor="let t of vehicleTypes" [value]="t.value">{{t.label}}</mat-option>
        </mat-select>
      </mat-form-field>
      <div class="invalid-feedback d-block" *ngIf="(f.type?.touched || submitted) && f.type?.errors?.required">
        Please select a vehicle type.
      </div>
    </div>
  </div>

  <!-- All other sections show only once type is selected -->
  <ng-container *ngIf="selectedType">

    <!-- ── DRIVERS / USE OF VEHICLE INFO ── -->
    <div class="alert alert-info mb-3" *ngIf="selectedType !== 'trailers'">
      <div class="row" *ngIf="selectedType === 'agricultural'">
        <div class="col-md-3"><strong>Drivers</strong></div>
        <div class="col-md-9">
          Any licenced driver (except where a fully trained driver over the age of 13 years uses an agricultural
          vehicle in a place where no licence is required by law).
        </div>
      </div>
      <div class="row" [ngClass]="{ 'mt-3': selectedType === 'agricultural' }">
        <div class="col-md-3"><strong>Use of Vehicle</strong></div>
        <div class="col-md-9">
          <span *ngIf="selectedType === 'agricultural'">
            Business use of the proposer including social, domestic and pleasure use, travel to and from the
            proposer's place of work and whilst hired or loaned to a Public Authority for the purpose of driving
            stone crushing plant or for snow clearance.
          </span>
          <span *ngIf="selectedType !== 'agricultural'">
            Business use of the proposer including social, domestic and pleasure use and travel to and from the
            proposer's place of work.
          </span>

          <div class="mt-2">
            <strong>The following exclusions apply as standard;</strong>
            <a class="btn btn-link btn-sm p-0 ml-2" (click)="showExclusions = !showExclusions">
              {{ showExclusions ? '− Hide exclusions' : '+ Show exclusions' }}
            </a>
          </div>
          <ul class="mt-2 mb-0" *ngIf="showExclusions">
            <li *ngFor="let e of standardExclusions">{{ e }}</li>
          </ul>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-md-12">
          <small>
            The driving restriction and extended use options selected below determine which drivers must be declared
            on the Drivers section. If this vehicle allows named drivers, or is used for personal/business use by
            anyone other than the proposer, you will need to provide that driver's details before you can complete
            the quote.
          </small>
        </div>
      </div>
    </div>

    <!-- ── VEHICLE DETAILS ── -->
    <div class="green_border_panel">
      <h6 class="text-white">Vehicle Details</h6>

      <!-- Agricultural: Road Registered -->
      <div class="row mb-2" *ngIf="selectedType === 'agricultural'">
        <div class="col-md-8">
          <h6 class="text-white">Is this vehicle road registered? <span class="text-danger">*</span></h6>
          <small class="text-white">Select <strong>No</strong> if not registered with the DVLA or declared SORN.</small>
          <mat-radio-group formControlName="is_road_registered" class="row mt-2" required>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
          </mat-radio-group>
          <div class="invalid-feedback d-block" style="font-size:13px;"
            *ngIf="(f.is_road_registered?.touched || submitted) && f.is_road_registered?.errors?.required">
            Please select an option.
          </div>
        </div>
      </div>

      <!-- Registration Number -->
      <div class="row mb-2"
        *ngIf="selectedType !== 'trailers' && (selectedType !== 'agricultural' || frmFields.get('is_road_registered')?.value === 'Yes')">
        <div class="col-md-5">
          <div class="input-group">
            <input type="text" formControlName="registration_number" placeholder="Registration Number"
              class="form-control form-control-sm text-uppercase"
              [ngClass]="{ 'is-invalid': (f.registration_number?.touched || submitted) && f.registration_number?.errors?.required }">
            <div class="input-group-append">
              <button type="button" class="btn btn-success btn-sm" (click)="lookupVehicle()">
                <i class="mdi mdi-magnify"></i> Lookup
              </button>
            </div>
          </div>
          <div class="invalid-feedback d-block" *ngIf="(f.registration_number?.touched || submitted) && f.registration_number?.errors?.required">
            Registration number is required.
          </div>
        </div>
      </div>

      <!-- VIN (trailers / agricultural not road registered) -->
      <div class="row mb-2"
        *ngIf="selectedType === 'trailers' || frmFields.get('is_road_registered')?.value === 'No'">
        <div class="col-md-5">
          <input type="text" formControlName="vin" placeholder="Reg / Identification No. (VIN)"
            class="form-control form-control-sm text-uppercase"
            [ngClass]="{ 'is-invalid': (f.vin?.touched || submitted) && f.vin?.errors?.required }">
          <small>Used as this vehicle's identification number on the policy.</small>
          <div class="invalid-feedback d-block" *ngIf="(f.vin?.touched || submitted) && f.vin?.errors?.required">
            Reg / identification number is required.
          </div>
        </div>
      </div>

      <!-- Make / Model / Year / Body Type -->
      <div class="row mb-2">
        <div class="col-md-3">
          <div class="form-group">
            <input type="text" formControlName="make" placeholder="Make" class="form-control form-control-sm" required
              [ngClass]="{ 'is-invalid': (f.make?.touched || submitted) && f.make?.errors?.required }">
            <div class="invalid-feedback" *ngIf="(f.make?.touched || submitted) && f.make?.errors?.required">Make is required.</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <input type="text" formControlName="model" placeholder="Model" class="form-control form-control-sm" required
              [ngClass]="{ 'is-invalid': (f.model?.touched || submitted) && f.model?.errors?.required }">
            <div class="invalid-feedback" *ngIf="(f.model?.touched || submitted) && f.model?.errors?.required">Model is required.</div>
          </div>
        </div>
        <div class="col-md-2" *ngIf="selectedType !== 'trailers'">
          <div class="form-group">
            <input type="text" formControlName="year" placeholder="Year" class="form-control form-control-sm" maxlength="4"
              [ngClass]="{ 'is-invalid': (f.year?.touched || submitted) && f.year?.errors?.required }">
            <div class="invalid-feedback" *ngIf="(f.year?.touched || submitted) && f.year?.errors?.required">Year is required.</div>
          </div>
        </div>
        <!-- No body type for horseboxes — the agri-api has no body_types.horseboxes list; they're classified by GVW instead -->
        <div class="col-md-4" *ngIf="staticData?.body_types && selectedType !== 'horseboxes'">
          <mat-form-field class="w-100">
            <mat-label>{{ selectedType === 'trailers' ? 'Trailer Type' : 'Body Type' }}</mat-label>
            <mat-select formControlName="body_type" required
              [ngClass]="{ 'is-invalid': (f.body_type?.touched || submitted) && f.body_type?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let b of (staticData.body_types[selectedType] || [])" [value]="b">{{b}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.body_type?.touched || submitted) && f.body_type?.errors?.required">{{ selectedType === 'trailers' ? 'Trailer type' : 'Body type' }} is required.</div>
        </div>
      </div>

      <!-- GVW + Value -->
      <div class="row mb-2">
        <div class="col-md-4"
          *ngIf="(selectedType === 'horseboxes' || selectedType === 'commercial') && staticData">
          <mat-form-field class="w-100">
            <mat-label>Gross Vehicle Weight</mat-label>
            <mat-select formControlName="weight" required
              [ngClass]="{ 'is-invalid': (f.weight?.touched || submitted) && f.weight?.errors?.required }">
              <mat-option value="">Select GVW</mat-option>
              <mat-option *ngFor="let w of (staticData.gross_vehicle_weights || [])" [value]="w">{{w}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.weight?.touched || submitted) && f.weight?.errors?.required">Gross vehicle weight is required.</div>
        </div>
        <div class="col-md-4">
          <div class="form-group mt-2">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-success text-white">&pound;</span>
              </div>
              <input type="number" formControlName="value" placeholder="Vehicle Value"
                class="form-control form-control-sm" required
                [ngClass]="{ 'is-invalid': (f.value?.touched || submitted) && f.value?.errors?.required }">
            </div>
            <div class="invalid-feedback d-block" *ngIf="(f.value?.touched || submitted) && f.value?.errors?.required">Vehicle value is required.</div>
          </div>
        </div>
      </div>

    </div><!-- end vehicle details -->

    <!-- ── COVER DETAILS (not trailers) ── -->
    <div class="green_border_panel mt-3" *ngIf="selectedType !== 'trailers'">
      <h6 class="text-white">Cover Details</h6>
      <div class="row" *ngIf="staticData?.cover_types">
        <div class="col-md-4">
          <mat-form-field class="w-100">
            <mat-label>Cover Type</mat-label>
            <mat-select formControlName="cover_type" required
              [ngClass]="{ 'is-invalid': (f.cover_type?.touched || submitted) && f.cover_type?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let c of (staticData.cover_types[selectedType] || [])" [value]="c">{{c}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.cover_type?.touched || submitted) && f.cover_type?.errors?.required">Cover type is required.</div>
        </div>
        <div class="col-md-4"
          *ngIf="selectedType === 'horseboxes' || selectedType === 'cars' || selectedType === 'commercial'">
          <mat-form-field class="w-100">
            <mat-label>Driving Restriction</mat-label>
            <mat-select formControlName="driving_restriction" required
              [ngClass]="{ 'is-invalid': (f.driving_restriction?.touched || submitted) && f.driving_restriction?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let d of filteredDrivingRestrictions" [value]="d">{{d}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.driving_restriction?.touched || submitted) && f.driving_restriction?.errors?.required">Driving restriction is required.</div>
        </div>
        <div class="col-md-4" *ngIf="selectedType === 'cars' || selectedType === 'commercial'">
          <h6 class="text-white">Is this vehicle registered limited use? <span class="text-danger">*</span></h6>
          <mat-radio-group formControlName="is_limited_use" class="row" required>
            <div class="col-md-6"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
            <div class="col-md-6"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
          </mat-radio-group>
          <div class="invalid-feedback d-block" style="font-size:13px;"
            *ngIf="(f.is_limited_use?.touched || submitted) && f.is_limited_use?.errors?.required">
            Please select an option.
          </div>
        </div>
      </div>

      <!-- Mileage / Tracker / NCD -->
      <div class="row mt-3"
        *ngIf="(selectedType === 'horseboxes' || selectedType === 'cars' || selectedType === 'commercial') && staticData">
        <!-- Horsebox: dropdown from mileage_options -->
        <div class="col-md-4" *ngIf="selectedType === 'horseboxes' && staticData?.mileage_options">
          <mat-form-field class="w-100">
            <mat-label>Annual Mileage</mat-label>
            <mat-select formControlName="mileage" required
              [ngClass]="{ 'is-invalid': (f.mileage?.touched || submitted) && f.mileage?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let m of staticData.mileage_options" [value]="m">{{m}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.mileage?.touched || submitted) && f.mileage?.errors?.required">Mileage is required.</div>
        </div>
        <!-- Car / Commercial: free numerical input -->
        <div class="col-md-4" *ngIf="selectedType === 'cars' || selectedType === 'commercial'">
          <div class="form-group mt-2">
            <input type="number" formControlName="mileage" placeholder="Annual Mileage (e.g. 5000)"
              class="form-control form-control-sm" min="0" required
              [ngClass]="{ 'is-invalid': (f.mileage?.touched || submitted) && f.mileage?.errors?.required }">
            <div class="invalid-feedback" *ngIf="(f.mileage?.touched || submitted) && f.mileage?.errors?.required">Mileage is required.</div>
          </div>
        </div>
        <div class="col-md-4" *ngIf="staticData.tracker_types">
          <mat-form-field class="w-100">
            <mat-label>Vehicle Tracker</mat-label>
            <mat-select formControlName="tracker" required
              [ngClass]="{ 'is-invalid': (f.tracker?.touched || submitted) && f.tracker?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let t of (staticData.tracker_types[selectedType] || [])" [value]="t">{{t}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.tracker?.touched || submitted) && f.tracker?.errors?.required">Tracker selection is required.</div>
        </div>
        <div class="col-md-4" *ngIf="ncdApplies && staticData.ncd_options">
          <mat-form-field class="w-100">
            <mat-label>No Claims Discount (Years)</mat-label>
            <mat-select formControlName="ncd" required
              [ngClass]="{ 'is-invalid': (f.ncd?.touched || submitted) && f.ncd?.errors?.required }">
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let n of staticData.ncd_options" [value]="n">{{n}}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="invalid-feedback d-block" *ngIf="(f.ncd?.touched || submitted) && f.ncd?.errors?.required">No claims discount is required.</div>
        </div>
      </div>
    </div>

    <!-- ── NAMED DRIVERS (when driving restriction requires it) ── -->
    <div class="green_border_panel mt-3" *ngIf="showDrivers">
      <h6 class="text-white">Drivers</h6>
      <div *ngIf="vehicle.drivers && vehicle.drivers.length > 0">
        <div class="row mb-2" *ngFor="let d of vehicle.drivers; let di = index">
          <div class="col-md-5">
            <span class="badge badge-light p-2">
              {{d.title}} {{d.first_name || 'No name provided'}} {{d.last_name}}
            </span>
          </div>
          <div class="col-md-2">
            <a (click)="removeDriver(di)" class="btn btn-danger btn-sm p-1">
              <i class="mdi mdi-delete"></i>
            </a>
          </div>
        </div>
      </div>
      <div *ngIf="!vehicle.drivers || vehicle.drivers.length === 0" class="mb-2">
        <small class="text-muted">No drivers assigned to this vehicle.</small>
      </div>
      <button type="button" class="btn btn-success btn-sm f-12" (click)="showAddDriverDialog = !showAddDriverDialog">
        <i class="mdi mdi-plus"></i> Add Driver
      </button>
      <div class="invalid-feedback d-block" *ngIf="submitted && (!vehicle.drivers || vehicle.drivers.length === 0)">
        Please add at least one driver for this vehicle's driving restriction.
      </div>
      <div class="mt-2" *ngIf="showAddDriverDialog">
        <app-ag-add-driver-dialog
          [drivers]="modelObj?.drivers || []"
          [staticData]="staticData"
          [authDetails]="authDetails"
          target="drivers"
          (driverAdded)="onDriverAdded($event)"
          (cancelled)="showAddDriverDialog = false">
        </app-ag-add-driver-dialog>
      </div>
    </div>

    <!-- ── EXTENDED USE (not trailers) ── -->
    <div class="green_border_panel mt-3" *ngIf="selectedType !== 'trailers'">
      <h6 class="text-white">Extended Use</h6>
      <small class="text-white">Please select any additional use required. Standard cover does not include the following.</small>
      <div class="mt-2" formGroupName="extended_use">
        <mat-checkbox formControlName="driver_business_use"
          *ngIf="selectedType === 'horseboxes' || selectedType === 'cars' || selectedType === 'commercial'" class="mb-2">
          Use by any other person to travel to and/or from their place of work and/or study and for any associated business use
        </mat-checkbox>

        <!-- Drivers requiring personal/business use must be identified before the quote
             can continue — they are sent to the insurer as `extended_drivers`. -->
        <div class="pl-3 mb-3" *ngIf="frmFields.get('extended_use.driver_business_use')?.value">
          <h6 class="text-white">Please select the driver(s) requiring personal business use.</h6>
          <div *ngIf="vehicle.extended_drivers && vehicle.extended_drivers.length > 0">
            <div class="row mb-2" *ngFor="let d of vehicle.extended_drivers; let edi = index">
              <div class="col-md-5">
                <span class="badge badge-light p-2">
                  {{d.title}} {{d.first_name || 'No name provided'}} {{d.last_name}}
                </span>
              </div>
              <div class="col-md-2">
                <a (click)="removeExtendedDriver(edi)" class="btn btn-danger btn-sm p-1">
                  <i class="mdi mdi-delete"></i>
                </a>
              </div>
            </div>
          </div>
          <button type="button" class="btn btn-success btn-sm f-12"
            (click)="showAddExtendedDriverDialog = !showAddExtendedDriverDialog">
            <i class="mdi mdi-plus"></i> Add Driver
          </button>
          <div class="invalid-feedback d-block"
            *ngIf="submitted && (!vehicle.extended_drivers || vehicle.extended_drivers.length === 0)">
            Please add at least one driver requiring personal business use.
          </div>
          <div class="mt-2" *ngIf="showAddExtendedDriverDialog">
            <app-ag-add-driver-dialog
              [drivers]="modelObj?.drivers || []"
              [staticData]="staticData"
              [authDetails]="authDetails"
              target="extended"
              (driverAdded)="onExtendedDriverAdded($event)"
              (cancelled)="showAddExtendedDriverDialog = false">
            </app-ag-add-driver-dialog>
          </div>
        </div>
        <mat-checkbox formControlName="contract_work" class="mb-2">
          Use in connection with the undertaking of contract work
        </mat-checkbox>
        <mat-checkbox formControlName="livestock_carriage" class="mb-2">
          Carriage of any horse, animal and/or other living creatures for hire and/or reward
        </mat-checkbox>
        <div class="row mt-2" *ngIf="frmFields.get('extended_use.livestock_carriage')?.value">
          <div class="col-md-8">
            <h6 class="text-white">Is this use required for the trade of Horse Transporter? <span class="text-danger">*</span></h6>
            <mat-radio-group formControlName="horse_transporter" class="row" required>
              <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
              <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
            </mat-radio-group>
            <div class="invalid-feedback d-block" style="font-size:13px;"
              *ngIf="(frmFields.get('extended_use.horse_transporter')?.touched || submitted) && frmFields.get('extended_use.horse_transporter')?.errors?.required">
              Please select an option.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── GREEN CARD / OWNERSHIP / OVERNIGHT (not trailers) ── -->
    <div class="green_border_panel mt-3" *ngIf="selectedType !== 'trailers'">

      <div class="row">
        <div class="col-md-6">
          <h6 class="text-white">Do you require an Annual Green Card? <span class="text-danger">*</span></h6>
          <mat-radio-group formControlName="green_card" class="row" required>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
          </mat-radio-group>
          <div class="invalid-feedback" style="font-size:13px;"
            *ngIf="(f.green_card?.touched || submitted) && f.green_card?.errors?.required">
            Please select an option.
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-md-6">
          <h6 class="text-white">Is the proposer the registered owner of this vehicle? <span class="text-danger">*</span></h6>
          <mat-radio-group formControlName="is_registered_owner" class="row" required>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
            <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
          </mat-radio-group>
          <div class="invalid-feedback" style="font-size:13px;"
            *ngIf="(f.is_registered_owner?.touched || submitted) && f.is_registered_owner?.errors?.required">
            Please select an option.
          </div>

          <div class="mt-3" *ngIf="frmFields.get('is_registered_owner')?.value === 'No' && staticData?.ownership_options">
            <h6 class="text-white">Please confirm who is the registered owner</h6>
            <mat-form-field class="w-100">
              <mat-label>Ownership</mat-label>
              <mat-select formControlName="owner" required
                [ngClass]="{ 'is-invalid': (f.owner?.touched || submitted) && f.owner?.errors?.required }">
                <mat-option value="">Select</mat-option>
                <mat-option *ngFor="let o of staticData.ownership_options" [value]="o">{{o}}</mat-option>
              </mat-select>
            </mat-form-field>
            <div class="form-group mt-2">
              <input type="text" formControlName="owner_name" placeholder="Name of registered owner"
                class="form-control form-control-sm">
            </div>
          </div>

          <div class="mt-3">
            <h6 class="text-white">
              Is this vehicle kept overnight at
              <span *ngIf="modelObj?.proposer?.address?.postcode">{{ modelObj.proposer.address.postcode | uppercase }}</span>
              <span *ngIf="!modelObj?.proposer?.address?.postcode">the correspondence address postcode</span>?
              <span class="text-danger">*</span>
            </h6>
            <mat-radio-group formControlName="is_kept_overnight" class="row" required>
              <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
              <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
            </mat-radio-group>
            <div class="invalid-feedback" style="font-size:13px;"
              *ngIf="(f.is_kept_overnight?.touched || submitted) && f.is_kept_overnight?.errors?.required">
              Please select an option.
            </div>
            <div class="mt-3" *ngIf="frmFields.get('is_kept_overnight')?.value === 'No'">
              <h6 class="text-white">Please provide the postcode(s) for where this vehicle is kept overnight</h6>
              <div formArrayName="overnight_addresses">
                <div *ngFor="let addressGroup of overnightAddresses.controls; let ai = index" [formGroupName]="ai"
                  class="mb-3 pb-2 overnight_address">
                  <div class="d-flex justify-content-between align-items-center">
                    <small class="text-white">Address {{ai + 1}}</small>
                    <a class="btn btn-danger btn-sm p-1" (click)="removeOvernightAddress(ai)"
                      *ngIf="overnightAddresses.length > 1" title="Remove this address">
                      <i class="mdi mdi-delete"></i>
                    </a>
                  </div>
                  <div class="input-group">
                    <input type="text" formControlName="postcode" placeholder="Overnight Storage Postcode"
                      class="form-control form-control-sm text-uppercase" required
                      [ngClass]="{ 'is-invalid': (addressGroup.get('postcode')?.touched || submitted) && addressGroup.get('postcode')?.errors?.required }">
                    <div class="input-group-append">
                      <button type="button" class="btn btn-success btn-sm" (click)="lookupPostcode(ai)"
                        [disabled]="addressLookups[ai]?.loading">
                        <span *ngIf="addressLookups[ai]?.loading" class="spinner-border spinner-border-sm mr-1"></span>
                        <i class="mdi mdi-magnify" *ngIf="!addressLookups[ai]?.loading"></i> Search
                      </button>
                    </div>
                  </div>
                  <div class="invalid-feedback d-block"
                    *ngIf="(addressGroup.get('postcode')?.touched || submitted) && addressGroup.get('postcode')?.errors?.required">
                    Postcode is required.
                  </div>
                  <div class="invalid-feedback d-block" *ngIf="addressLookups[ai]?.message">
                    {{ addressLookups[ai]?.message }}
                  </div>
                  <div class="row mt-2" *ngIf="addressLookups[ai]?.show">
                    <div class="col-md-8">
                      <mat-form-field class="w-100">
                        <mat-label>Select Address</mat-label>
                        <mat-select (selectionChange)="selectPostcodeAddress(ai, $event.value)">
                          <mat-option value="">— Select an address —</mat-option>
                          <mat-option *ngFor="let addr of addressLookups[ai]?.list" [value]="addr">{{addr.l || (addr.formatted_address ? addr.formatted_address.join(', ') : '')}}</mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>
                  </div>
                  <div class="mt-2 text-white" *ngIf="formattedAddress(ai)">
                    <i class="mdi mdi-map-marker text-success"></i> {{ formattedAddress(ai) }}
                  </div>
                  <div class="mt-2 text-white" *ngIf="addressLookups[ai]?.verified === true && !formattedAddress(ai)">
                    <i class="mdi mdi-check-circle text-success"></i> Postcode found.
                  </div>
                  <div class="mt-2">
                    <a class="btn btn-default btn-gray btn-sm" (click)="useCorrespondenceAddress(ai)"
                      *ngIf="modelObj?.proposer?.address?.postcode">
                      Use correspondence address
                    </a>
                    <a class="btn btn-default btn-gray btn-sm ml-2" (click)="clearOvernightAddress(ai)"
                      *ngIf="addressGroup.get('postcode')?.value">
                      Clear
                    </a>
                  </div>
                </div>
              </div>
              <div class="mt-2">
                <a class="btn btn-default btn-gray btn-sm" (click)="addAnotherAddress()"
                  *ngIf="overnightAddresses.length < maxOvernightAddresses">
                  Add another address
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── MODIFICATIONS (cars, commercial — excluded for horseboxes) ── -->
    <div class="green_border_panel mt-3"
      *ngIf="selectedType === 'cars' || selectedType === 'commercial'">
      <h6 class="text-white">Does the vehicle have any non-standard modifications? <span class="text-danger">*</span></h6>
      <mat-radio-group formControlName="has_modifications" class="row" required>
        <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="Yes">Yes</mat-radio-button></div></div>
        <div class="col-md-4"><div class="quote_radio w-90"><mat-radio-button value="No">No</mat-radio-button></div></div>
      </mat-radio-group>
      <div class="invalid-feedback" style="font-size:13px;"
        *ngIf="(f.has_modifications?.touched || submitted) && f.has_modifications?.errors?.required">
        Please select an option.
      </div>

      <ng-container *ngIf="frmFields.get('has_modifications')?.value === 'Yes'">
        <div formArrayName="mods" class="mt-3">
          <div *ngFor="let mod of modsArray.controls; let mi = index" class="row mb-2">
            <div class="col-md-6" *ngIf="staticData?.mod_types">
              <mat-form-field class="w-100">
                <mat-label>Modification {{mi + 1}}</mat-label>
                <mat-select [formControlName]="mi" required
                  [ngClass]="{ 'is-invalid': submitted && !mod.value }">
                  <mat-option value="">Select modification type</mat-option>
                  <mat-option *ngFor="let m of (staticData.mod_types[selectedType] || [])" [value]="m">{{m}}</mat-option>
                </mat-select>
              </mat-form-field>
              <div class="invalid-feedback d-block" *ngIf="submitted && !mod.value">
                Please select a modification type.
              </div>
            </div>
            <div class="col-md-2 mt-2">
              <a (click)="removeMod(mi)" class="btn btn-danger btn-sm p-1"><i class="mdi mdi-delete"></i></a>
            </div>
          </div>
        </div>
        <a (click)="addMod()" class="btn btn-success btn-sm mt-2">
          <i class="mdi mdi-plus"></i> Add Modification
        </a>
        <div class="invalid-feedback d-block" *ngIf="submitted && namedModCount === 0">
          Please add at least one modification.
        </div>
      </ng-container>
    </div>

    <!-- ── TRAILER COVER NOTE ── -->
    <div class="green_border_panel mt-3" *ngIf="selectedType === 'trailers'">
      <h6 class="text-white">Cover Information</h6>
      <p class="text-white"><strong>When cover applies:</strong> While attached to a vehicle listed on this policy, and continuing once detached until the trailer/implement is attached to another vehicle.</p>
      <p class="text-white"><strong>Cover, terms &amp; limitations:</strong> Same as the insured vehicle to which the trailer/implement is (or was last) attached.</p>
    </div>

  </ng-container>

</form>
